<template>
  <footer
    class="z-10 flex bg-white p-4 text-darkestGray md:justify-center md:p-10 lg:px-28"
  >
    <div
      class="mx-auto flex w-full max-w-7xl flex-col justify-between gap-2 md:gap-6 lg:flex-row"
    >
      <!-- Left Section -->
      <div class="flex w-full flex-col gap-2 md:flex-row md:gap-6 lg:w-3/5">
        <!-- O ALAB Section -->
        <div class="flex w-full flex-col gap-y-2 text-sm md:gap-y-6 lg:w-1/3">
          <details ref="detailsOAlab" class="group">
            <summary
              class="flex cursor-pointer items-center justify-between text-sm font-extrabold md:pointer-events-none md:px-2"
            >
              <span>O ALAB</span>
              <inline-svg
                :src="useAsset(`icons/base/angle-down.svg`)"
                class="ml-2 transform cursor-pointer transition-transform duration-200 group-hover:brightness-50 md:hidden"
                loading="lazy"
                width="13"
                height="8"
                alt="angle-down-icon"
              />
            </summary>

            <div class="mt-2 flex w-full flex-col gap-y-2 md:mt-5 md:gap-y-6">
              <a
                v-for="link in about"
                :key="link.name"
                class="px-2 hover:opacity-70"
                :href="getURL(link)"
                v-bind="link?.bind"
              >
                {{ link.name }}
              </a>
            </div>
          </details>

          <!-- Social Links (Visible on Desktop) -->
          <div class="hidden gap-2 px-2 md:flex">
            <a
              v-for="site in social"
              :key="site.alt"
              class="inline-block"
              :href="site.link"
            >
              <img
                class="h-5"
                :src="`/logos/${site.logo}`"
                :alt="site.alt"
                height="20"
                width="24"
              />
            </a>
          </div>
        </div>

        <!-- Divider (Visible on Mobile) -->
        <div
          class="box-border flex w-full flex-row items-center self-stretch border-b border-gray md:hidden"
        ></div>

        <!-- Dla Pacjenta Section -->
        <div class="flex w-full flex-col gap-y-2 text-sm md:gap-y-6 lg:w-1/3">
          <details ref="detailsPatient" class="group">
            <summary
              class="flex cursor-pointer items-center justify-between text-sm font-extrabold md:pointer-events-none md:px-2"
            >
              <span>Dla Pacjenta</span>
              <inline-svg
                :src="useAsset(`icons/base/angle-down.svg`)"
                class="ml-2 transform cursor-pointer transition-transform duration-200 group-hover:brightness-50 md:hidden"
                loading="lazy"
                width="13"
                height="8"
                alt="angle-down-icon"
              />
            </summary>

            <div class="mt-2 flex w-full flex-col gap-y-2 md:mt-5 md:gap-y-6">
              <a
                v-for="link in patient"
                :key="link.name"
                class="px-2 hover:opacity-70"
                :href="getURL(link)"
                v-bind="link?.bind"
              >
                {{ link.name }}
              </a>
            </div>
          </details>
        </div>

        <!-- Divider (Visible on Mobile) -->
        <div
          class="box-border flex w-full flex-row items-center self-stretch border-b border-gray md:hidden"
        ></div>

        <!-- Pomoc Section -->
        <div class="flex w-full flex-col gap-y-2 text-sm md:gap-y-6 lg:w-1/3">
          <!-- Pomoc Section -->
          <details ref="detailsPomoc" class="group">
            <summary
              class="flex cursor-pointer items-center justify-between text-sm font-extrabold md:pointer-events-none md:px-2"
            >
              <span>Pomoc</span>
              <img
                src="~/assets/icons/base/angle-down.svg"
                class="ml-2 transform cursor-pointer transition-transform duration-200 group-hover:brightness-50 md:hidden"
                loading="lazy"
                width="13"
                height="8"
                alt="angle-down-icon"
              />
            </summary>

            <div class="mt-2 flex w-full flex-col gap-y-2 md:mt-5 md:gap-y-6">
              <a
                v-for="link in help"
                :key="link.name"
                class="px-2 hover:opacity-70"
                :href="getURL(link)"
                v-bind="link?.bind"
              >
                {{ link.name }}
              </a>
              <span
                v-if="isZowieAvailable"
                class="cursor-pointer px-2 hover:opacity-70"
                @click="toggleZowie"
              >
                Chat
              </span>
            </div>
          </details>

          <!-- Divider (Visible on Mobile) -->
          <div
            class="box-border flex w-full flex-row items-center self-stretch border-b border-gray md:hidden"
          ></div>

          <!-- Social Links (Visible on Mobile) -->
          <div
            class="mt-2 flex max-w-[450px] justify-between gap-2 md:hidden md:px-2"
          >
            <a
              v-for="site in social"
              :key="site.alt"
              class="inline-block"
              :href="site.link"
            >
              <img
                class="h-6"
                :src="`/logos/${site.logo}`"
                :alt="site.alt"
                height="24"
                width="32"
              />
            </a>
          </div>
        </div>
      </div>

      <!-- Right Section -->
      <div
        class="mt-2 flex w-full flex-col items-start gap-2 md:mt-0 md:gap-6 lg:w-2/5"
      >
        <!-- Divider (Visible on Mobile) -->
        <div
          class="box-border flex w-full flex-row items-center self-stretch border-b border-gray md:hidden"
        ></div>

        <!-- ALAB Group Title -->
        <p class="text-sm font-extrabold">{{ $t('footer.alab_group') }}</p>

        <!-- ALAB Group Logos -->
        <div
          class="flex w-full flex-col items-start justify-start gap-2 md:flex-row md:gap-6"
        >
          <img
            v-for="company in ourGroup"
            :key="company.alt"
            class="mb-3 h-12 h-7 w-auto md:mb-0"
            :src="`/logos/${company.logo}`"
            :alt="company.alt"
            :height="company.height"
            :width="company.width"
          />
        </div>

        <!-- Divider (Visible on Mobile) -->
        <div
          class="box-border flex w-full flex-row items-center self-stretch border-b border-gray md:hidden"
        ></div>

        <!-- Payment Description -->
        <div class="w-full text-xs leading-5 lg:max-w-[500px]">
          {{ $t('footer.payment_description') }}
        </div>

        <!-- Payment Logos -->
        <!-- <div class="flex flex-row flex-wrap md:mr-auto">
          <nuxt-img
            src="/logos/payments.png"
            class="grayscale"
            alt="payments-logos"
            format="webp"
            loading="lazy"
            width="229"
            height="51"
          />
        </div> -->
      </div>
    </div>
  </footer>
</template>

<script setup>
import { useFeaturesStore } from '~~/stores/features'
import InlineSvg from 'vue-inline-svg'

const { t } = useI18n()

const featuresStore = useFeaturesStore()

// Refs for each <details> element
const detailsOAlab = ref(null)
const detailsPatient = ref(null)
const detailsPomoc = ref(null)

const isZowieAvailable = ref(false)
const isZowieOpen = ref(false)
const isZowieHidden = ref(false)

// Event handler functions for Zowie
const handleZowieOpen = () => {
  isZowieOpen.value = true
  sessionStorage.setItem('zowieClicked', 'true')
}
const handleZowieClose = () => {
  isZowieOpen.value = false
}
const handleZowieHide = () => {
  isZowieHidden.value = true
}
const handleZowieShow = () => {
  isZowieHidden.value = false
}
const handleZowieLoaded = () => {
  isZowieAvailable.value = true
}

const isLargeScreen = ref(false)

// Function to update the screen size state
const updateScreenSize = () => {
  isLargeScreen.value = window.innerWidth > 768
}

// Function to toggle <details> based on screen size
const toggleDetails = () => {
  const detailsElements = [
    detailsOAlab.value,
    detailsPatient.value,
    detailsPomoc.value,
  ]

  detailsElements.forEach((detail) => {
    if (isLargeScreen.value) {
      detail.setAttribute('open', '')
    } else {
      detail.removeAttribute('open')
    }
  })
}

watch(isLargeScreen, () => {
  toggleDetails()
})

// Set up event listeners on mount and clean up on unmount
onMounted(() => {
  updateScreenSize()
  toggleDetails()

  window.addEventListener('resize', updateScreenSize)
  // Initialize the state

  window.addEventListener('zowieLoaded', handleZowieLoaded)
  window.addEventListener('zowieOpen', handleZowieOpen)
  window.addEventListener('zowieClose', handleZowieClose)
  window.addEventListener('zowieHide', handleZowieHide)
  window.addEventListener('zowieShow', handleZowieShow)

  // If the chat has already loaded (e.g., if the script was injected before this component mounts)
  if (window.Chatbotize) {
    handleZowieLoaded()
  }
})

onUnmounted(() => {
  window.removeEventListener('resize', updateScreenSize)
  window.removeEventListener('zowieLoaded', handleZowieLoaded)
  window.removeEventListener('zowieOpen', handleZowieOpen)
  window.removeEventListener('zowieClose', handleZowieClose)
  window.removeEventListener('zowieHide', handleZowieHide)
  window.removeEventListener('zowieShow', handleZowieShow)
})

// Function to toggle chat visibility
const toggleZowie = () => {
  if (isZowieOpen.value) {
    Zowie.close()
    Zowie.hide()
  } else {
    Zowie.show()
    Zowie.open()
  }
}

const ourGroup = [
  {
    logo: 'alab_laboratoria.svg',
    alt: 'alab-laboratoria-logo',
    height: '36',
    width: '160',
  },
  { logo: 'alab_plus.svg', alt: 'alab-plus-logo', height: '36', width: '127' },
  // { logo: 'bruss.svg', alt: 'bruss-logo', height: '36', width: '' },
  // { logo: 'bcam.svg', alt: 'bcam-logo', height: '36', width: '' },
]

const social = [
  {
    logo: 'facebook.svg',
    alt: 'facebook-logo',
    link: 'https://www.facebook.com/ALABlaboratoria',
  },
  {
    logo: 'youtube.svg',
    alt: 'youtube-logo',
    link: 'https://www.youtube.com/c/ALABlaboratoriaALAB',
  },
  {
    logo: 'linkedin.svg',
    alt: 'linkedin-logo',
    link: 'https://www.linkedin.com/company/alab',
  },
  {
    logo: 'instagram.svg',
    alt: 'instagram-logo',
    link: 'https://www.instagram.com/alablaboratoria/',
  },
]

const getURL = (link) => {
  if (!link.localize) return link.url

  return useAbsoluteUrl(link.url)
}

const about = computed(() => [
  { name: t('layouts.about_us'), url: '/about-us', localize: true },
  {
    name: t('footer.terms_of_service'),
    url: '/static/regulamin',
    localize: true,
  },
  {
    name: t('footer.privacy_policy'),
    url: '/polityka-prywatnosci',
    localize: true,
  },
  {
    name: t('footer.personal_data_protection'),
    url: '/static/ochrona-danych-osobowych-w-grupie-kapitalowej-alab',
    localize: true,
  },
  {
    name: t('footer.signalist'),
    url: 'https://alab.vco.ey.com',
    localize: false,
  },
  {
    name: 'Kariera',
    url: 'https://kariera.alablaboratoria.pl/',
    localize: false,
  },
])
const patient = computed(() => [
  {
    name: t('layouts.receive_results'),
    url: useGenerateSharedURL('test-results'),
    localize: false,
  },
  {
    name: t('layouts.collection_points'),
    url: '/punkty-pobran',
    localize: true,
  },
  {
    name: t('layouts.catalog'),
    url: featuresStore.isEnabled('show_new_catalog')
      ? '/katalog-badan/badania'
      : useGenerateSharedURL(),
    localize: false,
  },
  {
    name: t('layouts.cookies_panel'),
    url: 'javascript:Optanon.ToggleInfoDisplay()',
    localize: false,
  },
  {
    name: 'Formularze dla Pacjenta',
    url: '/static/formularze-dla-pacjenta',
    localize: true,
  },
  {
    name: 'Zarezerwuj wizytę w Punkcie Pobrań',
    url: '/rezerwacja',
    localize: true,
  },
  {
    name: 'Interpretacja wyników',
    url: '/interpretacja-wynikow',
    localize: true,
  },
])
const help = computed(() => [
  { name: t('layouts.contact'), url: '/contact', localize: true },
  {
    name: t('layouts.patient_info'),
    url: '/informacje-dla-pacjenta',
    localize: true,
  },
  {
    name: 'Przygotowania do badań',
    url: '/przygotowanie-do-badania',
    localize: true,
  },
  { name: t('layouts.faq'), url: '/faq', localize: true },
  {
    name: t('footer.returns'),
    url: 'https://alabreklamacje.pl/',
    localize: false,
  },
  { name: t('footer.withdrawals'), url: '/odstapienia', localize: true },
])
</script>

<style scoped>
/* Rotate the arrow icon when the <details> element is open */
details[open] summary img {
  transform: rotate(180deg);
}
details summary::-webkit-details-marker {
  display: none;
}
</style>
